import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { ALERT_MESSAGES, FEEDBACK_FORM_TYPE, TOAST_STATUSES } from 'constants/ProjectConstants';
import { sendRequestedFeedback } from 'constants/mixpanelHelper';

import { requestNewFeedback } from 'services/continuousFeedback';

import { useRequestedFeedbackData } from '../slice/feedbackSlice';

export const useSaveRequestFeedback = ({ source, onClose }: { source: string; onClose: () => void }) => {
  const [isSaving, setIsSaving] = useState(false);
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const queryClient = useQueryClient();

  const toast = useToast();

  const { goal, questionText, receiverEmployeeId, visibility, providerEmployees } = useRequestedFeedbackData();

  const onSave = async () => {
    setIsSaving(true);
    let body = {
      feedbackType: FEEDBACK_FORM_TYPE.REQUESTED,
      visibility,
      receiverEmployeeId,
      requesterEmployeeId: null,
      providerEmployeeId: '',
      questionText,
    };

    const isRequestingFeedbackForSelf = receiverEmployeeId === loggedInEmployeeId;

    if (goal) {
      body = {
        ...body,
        // @ts-expect-error goal is of never type
        goal,
      };
    }

    const providerIds = providerEmployees.map((employee) => employee?.id);

    sendRequestedFeedback({
      eventProperties: {
        source,
        visibility,
        association: goal,
        requestedFor: isRequestingFeedbackForSelf ? 'self' : 'others',
      },
    });

    try {
      await requestNewFeedback({
        body,
        providerIds,
        is_new_panel: true,
      });
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.REQUEST.SUCCESS,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      console.error('Error sharing feedback ', error);
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.REQUEST.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    } finally {
      setIsSaving(false);
      queryClient.invalidateQueries({ queryKey: ['CFTable'] });
      queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
    }
  };

  return {
    onSave,
    isSaving,
  };
};
